import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthConfig, clearAuth } from '../utils/auth';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Package, Ship, Users, LogOut, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [container, setContainer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, containerRes, ordersRes, productsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/auth/me`, getAuthConfig()),
          axios.get(`${BACKEND_URL}/api/containers/active`),
          axios.get(`${BACKEND_URL}/api/orders`, getAuthConfig()),
          axios.get(`${BACKEND_URL}/api/products`)
        ]);

        setUser(userRes.data);
        setContainer(containerRes.data);
        setOrders(ordersRes.data);
        
        const productsMap = {};
        productsRes.data.forEach(p => {
          productsMap[p.product_id] = p;
        });
        setProducts(productsMap);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, getAuthConfig());
      clearAuth();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const fillPercentage = container ? (container.current_fill / container.total_capacity) * 100 : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <nav className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img src="/bluecrate-logo.jpg" alt="BlueCrate" className="h-10" />
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                {user?.picture && (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full"
                    data-testid="user-avatar"
                  />
                )}
                <span className="font-medium" data-testid="user-name">{user?.name}</span>
              </div>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-muted-foreground hover:text-foreground"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-chivo font-black text-4xl md:text-5xl mb-12" data-testid="dashboard-title">
          Your Dashboard
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          <div className="p-6 border border-border bg-card rounded-sm" data-testid="stat-card-orders">
            <div className="flex items-center justify-between mb-2">
              <Package className="w-8 h-8 text-primary" />
            </div>
            <div className="font-chivo font-bold text-3xl mb-1">{orders.length}</div>
            <div className="text-sm text-muted-foreground">Total Orders</div>
          </div>

          <div className="p-6 border border-border bg-card rounded-sm" data-testid="stat-card-spending">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-primary" />
            </div>
            <div className="font-chivo font-bold text-3xl mb-1">
              ${orders.reduce((sum, order) => sum + order.total_price, 0).toFixed(2)}
            </div>
            <div className="text-sm text-muted-foreground">Total Spending</div>
          </div>

          <div className="p-6 border border-border bg-card rounded-sm" data-testid="stat-card-sqft">
            <div className="flex items-center justify-between mb-2">
              <Ship className="w-8 h-8 text-primary" />
            </div>
            <div className="font-chivo font-bold text-3xl mb-1">
              {orders.reduce((sum, order) => sum + order.quantity, 0)}
            </div>
            <div className="text-sm text-muted-foreground">Total SqFt Ordered</div>
          </div>
        </div>

        {container && (
          <div className="mb-12 p-8 border border-border bg-card rounded-sm" data-testid="container-status">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-chivo font-bold text-2xl">Active Container Status</h2>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Users className="w-4 h-4" />
                <span>{container.participants} participants</span>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between mb-2 text-sm">
                <span className="text-muted-foreground">Capacity Used</span>
                <span className="font-chivo font-bold">
                  {container.current_fill} / {container.total_capacity} sqft
                </span>
              </div>
              <Progress value={fillPercentage} className="h-4" data-testid="container-progress" />
              <div className="mt-2 text-sm text-muted-foreground">
                {fillPercentage.toFixed(1)}% Full
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground mb-1">Origin</div>
                <div className="font-medium">{container.origin_port}</div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1">Destination</div>
                <div className="font-medium">{container.destination_port}</div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1">Est. Arrival</div>
                <div className="font-medium">{container.arrival_date}</div>
              </div>
            </div>
          </div>
        )}

        <div>
          <h2 className="font-chivo font-bold text-2xl mb-6" data-testid="orders-title">Your Orders</h2>
          
          {orders.length === 0 ? (
            <div className="p-12 border border-border bg-card rounded-sm text-center">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No orders yet</p>
              <Button
                onClick={() => navigate('/products')}
                className="bg-primary hover:bg-primary/90"
                data-testid="browse-products-button"
              >
                Browse Products
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => {
                const product = products[order.product_id];
                return (
                  <div
                    key={order.order_id}
                    className="p-6 border border-border bg-card rounded-sm"
                    data-testid={`order-${order.order_id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-chivo font-semibold text-lg mb-2">
                          {product?.name || 'Unknown Product'}
                        </h3>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <div>Quantity: {order.quantity} sqft</div>
                          <div>Price: ${order.price_per_unit.toFixed(2)}/sqft</div>
                          <div>Status: <span className="capitalize">{order.status}</span></div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-chivo font-bold text-2xl text-primary">
                          ${order.total_price.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
